import { useState, useEffect, useCallback } from 'react';
import RotatingText from '../components/RotatingText';
import Footer from '../components/Footer';
import SEO, { websiteSchema } from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { signInWithPhoneNumber, RecaptchaVerifier, signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Scissors, Sparkles, Lock, CheckCircle, Star, Users, Shield, Clock, MapPin, Zap, Gift, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import Logo from '../components/Logo';
import axios from 'axios';

// Use relative path for Firebase Functions
const API = '/api';

function Landing() {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [name, setName] = useState('');
  const [gender, setGender] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [discountInfo, setDiscountInfo] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  // Fetch discount eligibility for offer banner
  useEffect(() => {
    const fetchDiscountInfo = async () => {
      try {
        const response = await axios.get(`${API}/salon/discount-eligibility`);
        setDiscountInfo(response.data);
      } catch (error) {
        console.log('Discount info not available');
      }
    };
    fetchDiscountInfo();
  }, [API]);

  // Cleanup reCAPTCHA on unmount
  useEffect(() => {
    return () => {
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {
          // Ignore cleanup errors
        }
        window.recaptchaVerifier = null;
      }
    };
  }, []);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Setup reCAPTCHA with better error handling
  const setupRecaptcha = useCallback(() => {
    // Clear existing verifier first
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear();
      } catch (e) {
        // Ignore
      }
      window.recaptchaVerifier = null;
    }

    // Create new verifier
    window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
      size: 'invisible',
      callback: () => {
        console.log('reCAPTCHA verified');
      },
      'expired-callback': () => {
        console.log('reCAPTCHA expired, resetting...');
        window.recaptchaVerifier = null;
      }
    });

    return window.recaptchaVerifier;
  }, []);

  // Google Sign-In for customers only
  const handleGoogleSignIn = async () => {
    if (!gender) {
      toast.error('Please select your gender first');
      return;
    }
    
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      localStorage.setItem('userRole', 'customer');
      localStorage.setItem('userName', user.displayName || '');
      localStorage.setItem('userEmail', user.email || '');
      localStorage.setItem('firebaseUid', user.uid);
      localStorage.setItem('userGender', gender);
      
      // Track login
      try {
        await axios.post(`${API}/auth/track-login`, {
          phone: user.phoneNumber || user.email,
          name: user.displayName,
          firebase_uid: user.uid,
          login_method: 'google'
        });
      } catch (e) {
        console.error('Login tracking error:', e);
      }
      
      toast.success('Login successful!');
      navigate('/customer');
    } catch (error) {
      console.error('Google Sign-In error:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        toast.error('Sign-in cancelled');
      } else if (error.code === 'auth/popup-blocked') {
        toast.error('Popup blocked. Please allow popups.');
      } else {
        toast.error('Google Sign-In failed');
      }
    }
    setLoading(false);
  };

  const handleSendOTP = async () => {
    if (!phone || phone.length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    try {
      const appVerifier = setupRecaptcha();
      const phoneNumber = `+91${phone}`;
      
      const result = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      setConfirmationResult(result);
      setOtpSent(true);
      setResendTimer(60); // 60 second cooldown
      toast.success('OTP sent to +91 ' + phone);
    } catch (error) {
      console.error('Error sending OTP:', error);
      
      // Reset reCAPTCHA on error
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {}
        window.recaptchaVerifier = null;
      }

      // User-friendly error messages
      if (error.code === 'auth/too-many-requests') {
        toast.error('Too many attempts. Please try again in a few minutes.');
      } else if (error.code === 'auth/invalid-phone-number') {
        toast.error('Invalid phone number. Please check and try again.');
      } else if (error.code === 'auth/captcha-check-failed') {
        toast.error('Verification failed. Please refresh the page.');
      } else if (error.code === 'auth/network-request-failed') {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error('Failed to send OTP. Please try again.');
      }
    }
    setLoading(false);
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    
    // Reset state for resend
    setConfirmationResult(null);
    setOtp('');
    setOtpSent(false);
    
    // Small delay then send
    setTimeout(() => handleSendOTP(), 100);
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length < 6) {
      toast.error('Please enter 6-digit OTP');
      return;
    }

    if (!confirmationResult) {
      toast.error('Please request OTP first');
      return;
    }

    if (selectedRole === 'customer' && !gender) {
      toast.error('Please select your gender');
      return;
    }

    setLoading(true);
    try {
      const result = await confirmationResult.confirm(otp);
      const user = result.user;

      localStorage.setItem('userRole', selectedRole);
      localStorage.setItem('userPhone', phone);
      localStorage.setItem('userName', name);
      localStorage.setItem('firebaseUid', user.uid);
      if (gender) {
        localStorage.setItem('userGender', gender);
      }

      // Track login
      try {
        await axios.post(`${API}/auth/track-login`, {
          phone: phone,
          name: name,
          firebase_uid: user.uid,
          login_method: 'otp'
        });
      } catch (e) {
        console.error('Login tracking error:', e);
      }

      toast.success('Login successful!');

      if (selectedRole === 'customer') {
        navigate('/customer');
      } else {
        navigate('/salon/partner-setup', { state: { phone, firebaseUid: user.uid, otpVerified: true } });
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      
      if (error.code === 'auth/code-expired') {
        toast.error('OTP expired. Please request a new one.');
        setConfirmationResult(null);
        setOtp('');
        setOtpSent(false);
      } else if (error.code === 'auth/invalid-verification-code') {
        toast.error('Invalid OTP. Please check and try again.');
      } else {
        toast.error('Verification failed. Please try again.');
      }
    }
    setLoading(false);
  };

  const handleBack = () => {
    // Reset all state
    setShowAuth(false);
    setConfirmationResult(null);
    setOtp('');
    setPhone('');
    setName('');
    setGender('');
    setOtpSent(false);
    setResendTimer(0);
    
    // Clear reCAPTCHA
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear();
      } catch (e) {}
      window.recaptchaVerifier = null;
    }
  };

  return (
    <div className="min-h-screen page-enter" style={{ background: '#fffaf8' }}>
      <div id="recaptcha-container"></div>
      <SEO
        title="Book Salon & Parlour Appointments Near You"
        description="Book trusted salons and parlours near you instantly. Verified salons, real-time slot booking, transparent pricing, no advance payment. India's premium salon booking platform."
        path="/"
        schema={websiteSchema}
      />

      {/* ── Premium Sticky Nav ──────────────────────────────────── */}
      <div className="fixed top-0 left-0 right-0 nav-glass z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center animate-fade-in">
            <Logo size="small" />
          </div>
          <div className="flex items-center gap-2 animate-fade-in stagger-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/help')}
              className="text-gray-600 hover:text-red-600 text-xs sm:text-sm rounded-xl transition-all hidden sm:flex"
            >
              ❓ Help
            </Button>
            <Button
              data-testid="be-salon-partner-btn"
              variant="outline"
              size="sm"
              onClick={() => { setSelectedRole('salon'); setShowAuth(true); }}
              className="text-purple-600 border-purple-200 hover:bg-purple-50 hover:border-purple-400 text-xs sm:text-sm rounded-xl transition-all"
            >
              <Scissors className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              <span className="hidden sm:inline">Be a </span>Partner
            </Button>
            <Button
              data-testid="admin-access-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/login')}
              className="text-gray-400 hover:text-gray-700 hidden sm:flex"
            >
              <Lock className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {!showAuth ? (
        <>
          {/* ════════════════════════════════════════════════════
              PROMO BANNER — 3 Month Free Onboarding
          ════════════════════════════════════════════════════ */}
          <div
            className="w-full px-4 pt-20 sm:pt-24"
            style={{ background: 'linear-gradient(180deg,#fff5f2 0%,#fff5f2 100%)' }}
          >
            <div
              onClick={() => { setSelectedRole('salon'); setShowAuth(true); }}
              className="container mx-auto max-w-2xl cursor-pointer rounded-2xl overflow-hidden shadow-lg animate-fade-up"
              style={{
                background: 'linear-gradient(120deg,#e11d48 0%,#be123c 45%,#7c2d12 100%)',
                boxShadow: '0 10px 30px rgba(225,29,72,0.35)',
              }}
            >
              <div className="relative px-5 py-4 sm:px-7 sm:py-5 flex items-center gap-4">
                <div
                  className="flex-shrink-0 flex items-center justify-center rounded-xl"
                  style={{ width: 52, height: 52, background: 'rgba(255,255,255,0.15)' }}
                >
                  <Gift className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <div className="inline-flex items-center gap-1 mb-1">
                    <span
                      className="text-[10px] font-black tracking-wide text-rose-700 bg-white px-2 py-0.5 rounded-full"
                    >
                      LIMITED TIME
                    </span>
                  </div>
                  <h3 className="text-white font-black leading-tight text-lg sm:text-xl" style={{ fontFamily: "'Syne',sans-serif" }}>
                    3 Months FREE Onboarding
                  </h3>
                  <p className="text-rose-100 text-xs sm:text-sm mt-0.5">
                    Salon & Parlour owners — list free for 3 months. No charges.
                  </p>
                </div>
                <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-white font-bold text-sm">
                  Join Now <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          </div>

          {/* ════════════════════════════════════════════════════
              SECTION 1 — HERO
          ════════════════════════════════════════════════════ */}
          <section className="pt-8 sm:pt-10 pb-10 px-4" style={{ background: 'linear-gradient(180deg,#fff5f2 0%,#fffaf8 70%,#ffffff 100%)' }}>
            <div className="container mx-auto max-w-2xl text-center">
              {discountInfo?.eligible && (
                <div className="inline-flex items-center gap-1.5 bg-rose-50 border border-rose-200 text-rose-600 px-3 py-1.5 rounded-full text-xs font-bold mb-5 animate-fade-up">
                  <Sparkles className="w-3.5 h-3.5" /> PREMIUM SALON EXPERIENCE
                </div>
              )}
              {!discountInfo?.eligible && (
                <div className="inline-flex items-center gap-1.5 bg-rose-50 border border-rose-200 text-rose-600 px-3 py-1.5 rounded-full text-xs font-bold mb-5 animate-fade-up">
                  <Sparkles className="w-3.5 h-3.5" /> PREMIUM SALON EXPERIENCE
                </div>
              )}

              <h1 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4 leading-tight animate-fade-up stagger-1" style={{ fontFamily: "'Syne',sans-serif" }}>
                Discover Trusted<br/>
                Salons <span className="gradient-text">Near You</span>
              </h1>

              <p className="text-gray-500 text-base mb-8 animate-fade-up stagger-2">
                Verified salons, instant booking, transparent pricing.
              </p>

              <div className="flex flex-col gap-3 max-w-sm mx-auto mb-8 animate-fade-up stagger-3">
                <Button
                  data-testid="hero-book-now-btn"
                  size="lg"
                  onClick={() => { setSelectedRole('customer'); setShowAuth(true); }}
                  className="btn-book text-white py-6 text-base font-bold rounded-2xl flex items-center justify-between px-6"
                >
                  <span className="flex items-center gap-2"><Sparkles className="w-5 h-5" /> Find Salons Near You</span>
                  <ArrowRight className="w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => { setSelectedRole('salon'); setShowAuth(true); }}
                  className="py-6 text-base font-bold rounded-2xl border-2 border-rose-200 text-rose-600 hover:bg-rose-50 flex items-center justify-between px-6"
                >
                  <span className="flex items-center gap-2">🏪 Become a Salon Partner</span>
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </div>

              {/* Quick search teaser card */}
              <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-5 max-w-md mx-auto text-left mb-8 animate-fade-up stagger-4">
                <p className="text-sm font-bold text-gray-800 flex items-center gap-1.5 mb-1">
                  <Sparkles className="w-4 h-4 text-amber-500" /> Find your perfect salon
                </p>
                <p className="text-xs text-gray-400 mb-4">Quick • Easy • Trusted</p>
                <div className="flex items-center gap-3 py-3 border-b border-gray-100">
                  <Scissors className="w-4 h-4 text-rose-400 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] text-gray-400">Service</p>
                    <p className="text-sm font-medium text-gray-700">Hair Color</p>
                  </div>
                </div>
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-rose-400 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] text-gray-400">Near you</p>
                      <p className="text-sm font-medium text-gray-700">Current Location</p>
                    </div>
                  </div>
                </div>
                <Button
                  onClick={() => { setSelectedRole('customer'); setShowAuth(true); }}
                  className="w-full mt-2 bg-red-600 hover:bg-red-700 rounded-2xl py-5"
                >
                  Search Salons
                </Button>
              </div>

              {/* Trust badges */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 max-w-2xl mx-auto mb-8 animate-fade-up stagger-5">
                {[
                  { icon: CheckCircle, label: 'Verified Salons', color: 'text-green-600', bg: 'bg-green-50' },
                  { icon: Clock, label: 'Easy Booking', color: 'text-blue-600', bg: 'bg-blue-50' },
                  { icon: Gift, label: 'No Hidden Fees', color: 'text-purple-600', bg: 'bg-purple-50' },
                  { icon: Star, label: 'Top Rated Stylists', color: 'text-amber-600', bg: 'bg-amber-50' },
                ].map(({ icon: Icon, label, color, bg }, i) => (
                  <div key={i} className={`flex flex-col items-center justify-center gap-1.5 ${bg} rounded-2xl p-3 card-hover`}>
                    <Icon className={`w-5 h-5 ${color}`} />
                    <span className={`font-semibold text-[11px] ${color} text-center leading-tight`}>{label}</span>
                  </div>
                ))}
              </div>

              {discountInfo?.eligible && (
                <div className="bg-gradient-to-r from-rose-50 to-orange-50 border border-rose-100 rounded-2xl p-4 flex items-center justify-between gap-3 max-w-md mx-auto mb-8 animate-fade-up stagger-6">
                  <div className="flex items-center gap-2.5 text-left">
                    <Zap className="w-5 h-5 text-rose-500 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-gray-800">Book Now — It's Free</p>
                      <p className="text-xs text-gray-500">No hidden charges • Free to book</p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-rose-400 flex-shrink-0" />
                </div>
              )}

              {/* Social proof bar */}
              <div className="flex items-center justify-center gap-4 text-sm animate-fade-up">
                <div className="flex -space-x-2">
                  {['👩','👨','👩‍🦱'].map((e,i)=>(
                    <div key={i} className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-sm">{e}</div>
                  ))}
                </div>
                <p className="text-gray-500 text-xs">Loved by <b className="text-gray-800">50,000+</b> Happy Customers</p>
                <div className="flex items-center gap-1 text-amber-500 font-bold text-xs">
                  <Star className="w-3.5 h-3.5 fill-current" /> 4.8/5
                </div>
              </div>
            </div>
          </section>

          {/* ════════════════════════════════════════════════════
              SECTION 2 — WHY CHOOSE US
          ════════════════════════════════════════════════════ */}
          <section className="py-8 px-4 bg-white">
            <div className="container mx-auto max-w-2xl">
              <h2 className="text-2xl sm:text-3xl font-black text-center text-gray-900 mb-6" style={{ fontFamily: "'Syne',sans-serif" }}>
                Smart <span className="gradient-text">Features</span>
              </h2>
              <div className="grid grid-cols-1 gap-4 mb-4">
                {/* AI Booking */}
                <div
                  onClick={() => { setSelectedRole('customer'); setShowAuth(true); }}
                  className="cursor-pointer rounded-2xl p-5 flex items-center gap-4 card-hover"
                  style={{ background: 'linear-gradient(120deg,#e11d48 0%,#be123c 100%)', boxShadow: '0 8px 24px rgba(225,29,72,0.28)' }}
                >
                  <div style={{ fontSize: 34 }}>🤖</div>
                  <div className="flex-1">
                    <div style={{ display: 'inline-block', background: '#fff', color: '#e11d48', fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 20, marginBottom: 4 }}>AI POWERED</div>
                    <h3 className="text-white font-black text-lg leading-tight" style={{ fontFamily: "'Syne',sans-serif" }}>Book with AI Assistant</h3>
                    <p className="text-rose-100 text-xs mt-0.5">Bas type karo "haircut chahiye" — AI salon dhoondh ke book kar dega.</p>
                  </div>
                  <span className="text-white text-2xl">→</span>
                </div>

                {/* Style Finder */}
                <div
                  onClick={() => { setSelectedRole('customer'); setShowAuth(true); }}
                  className="cursor-pointer rounded-2xl p-5 flex items-center gap-4 card-hover"
                  style={{ background: 'linear-gradient(120deg,#7c3aed 0%,#4c1d95 100%)', boxShadow: '0 8px 24px rgba(124,58,237,0.28)' }}
                >
                  <div style={{ fontSize: 34 }}>✨</div>
                  <div className="flex-1">
                    <div style={{ display: 'inline-block', background: '#fff', color: '#7c3aed', fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 20, marginBottom: 4 }}>NEW</div>
                    <h3 className="text-white font-black text-lg leading-tight" style={{ fontFamily: "'Syne',sans-serif" }}>Find Your Perfect Hairstyle</h3>
                    <p style={{ color: '#ede9fe' }} className="text-xs mt-0.5">Selfie lo → face shape detect → best hairstyles for you.</p>
                  </div>
                  <span className="text-white text-2xl">→</span>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 px-4 bg-white">
            <div className="container mx-auto max-w-2xl">
              <h2 className="text-2xl sm:text-3xl font-black text-center text-gray-900 mb-8" style={{ fontFamily: "'Syne',sans-serif" }}>
                Why Choose <span className="gradient-text">BookYourSalons?</span>
              </h2>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: Shield, title: 'Verified Salons', desc: 'All salons are verified & background checked for your safety.', color: 'text-rose-500', bg: 'bg-rose-50' },
                  { icon: Clock, title: 'Instant Booking', desc: 'Book in seconds and get instant confirmation on the go.', color: 'text-indigo-500', bg: 'bg-indigo-50' },
                  { icon: Gift, title: 'No Hidden Fees', desc: '100% transparent pricing. Pay what you see, nothing extra.', color: 'text-pink-500', bg: 'bg-pink-50' },
                  { icon: Star, title: 'Top Rated Stylists', desc: 'Choose from top rated stylists and read real customer reviews.', color: 'text-amber-500', bg: 'bg-amber-50' },
                ].map(({ icon: Icon, title, desc, color, bg }, i) => (
                  <div key={i} className="border border-gray-100 rounded-2xl p-4 card-hover">
                    <div className={`w-11 h-11 rounded-xl ${bg} flex items-center justify-center mb-3`}>
                      <Icon className={`w-5 h-5 ${color}`} />
                    </div>
                    <p className="font-bold text-sm text-gray-900 mb-1">{title}</p>
                    <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ════════════════════════════════════════════════════
              SECTION 3 — POPULAR SERVICES
          ════════════════════════════════════════════════════ */}
          <section className="py-12 px-4" style={{ background: '#fafafa' }}>
            <div className="container mx-auto max-w-2xl">
              <h2 className="text-xl font-black text-gray-900 mb-5" style={{ fontFamily: "'Syne',sans-serif" }}>
                Popular Services
              </h2>
              <div className="flex gap-4 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
                {[
                  { emoji: '✂️', label: 'Hair Cut', bg: '#fde8e8' },
                  { emoji: '🎨', label: 'Hair Color', bg: '#ede5fb' },
                  { emoji: '💆', label: 'Hair Spa', bg: '#e1f7ec' },
                  { emoji: '✨', label: 'Keratin', bg: '#fdeee0' },
                  { emoji: '💅', label: 'Makeup', bg: '#fce4ec' },
                ].map(({ emoji, label, bg }) => (
                  <div key={label} className="flex flex-col items-center gap-2 flex-shrink-0 min-w-[64px]">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center text-2xl" style={{ background: bg }}>
                      {emoji}
                    </div>
                    <span className="text-xs font-semibold text-gray-700">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ════════════════════════════════════════════════════
              SECTION 4 — HOW IT WORKS
          ════════════════════════════════════════════════════ */}
          <section className="py-14 px-4 bg-white">
            <div className="container mx-auto max-w-2xl">
              <h2 className="text-2xl font-black text-center text-gray-900 mb-8" style={{ fontFamily: "'Syne',sans-serif" }}>
                How It Works?
              </h2>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { num: 1, icon: '🔍', title: 'Search', desc: 'Find salons near you and choose your favorite service.' },
                  { num: 2, icon: '📅', title: 'Book', desc: 'Pick your slot and book instantly for free.' },
                  { num: 3, icon: '✅', title: 'Relax', desc: 'Visit the salon and enjoy your premium experience.' },
                ].map(({ num, icon, title, desc }) => (
                  <div key={num} className="text-center">
                    <div className="relative mx-auto mb-3 w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center text-xl">
                      {icon}
                      <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 text-white text-[10px] font-bold flex items-center justify-center">{num}</span>
                    </div>
                    <p className="font-bold text-sm text-gray-900 mb-1">{title}</p>
                    <p className="text-[11px] text-gray-500 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>

              {/* Salon Owner CTA */}
              <div className="mt-10 bg-gradient-to-r from-rose-50 to-pink-50 rounded-3xl p-6 flex items-center justify-between gap-4">
                <div>
                  <p className="font-black text-gray-900 text-base mb-1">Salon Owners?</p>
                  <p className="text-xs text-gray-500 mb-3">Grow your business with us</p>
                  <Button
                    data-testid="salon-partner-card"
                    onClick={() => { setSelectedRole('salon'); setShowAuth(true); }}
                    className="bg-red-600 hover:bg-red-700 rounded-xl text-xs px-4 py-2 h-auto"
                  >
                    Become a Partner <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
                <div className="text-5xl flex-shrink-0">🏪</div>
              </div>
            </div>
          </section>

          {/* ════════════════════════════════════════════════════
              SECTION 5 — TESTIMONIAL + STATS
          ════════════════════════════════════════════════════ */}
          <section className="py-14 px-4" style={{ background: '#fafafa' }}>
            <div className="container mx-auto max-w-2xl">
              <h2 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-2" style={{ fontFamily: "'Syne',sans-serif" }}>
                <span className="text-rose-300 text-2xl">"</span> What Our Customers Say
              </h2>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-8">
                <div className="flex gap-0.5 text-amber-400 mb-2">
                  {[1,2,3,4,5].map(i=> <Star key={i} className="w-3.5 h-3.5 fill-current" />)}
                </div>
                <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                  "Amazing experience! Found the best salon near me and booked instantly. Super easy and totally worth it."
                </p>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center text-sm">👩</div>
                  <div>
                    <p className="text-xs font-bold text-gray-800">Priya Mehta</p>
                    <p className="text-[10px] text-gray-400">Delhi</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl py-8 px-6">
                <p className="text-center text-gray-400 text-xs font-semibold mb-5 uppercase tracking-wider">Trusted by Thousands</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 text-center">
                  {[
                    { num: '10K+', label: 'Verified Salons', color: 'text-rose-400' },
                    { num: '50K+', label: 'Happy Customers', color: 'text-purple-400' },
                    { num: '100K+', label: 'Bookings Done', color: 'text-amber-400' },
                    { num: '50+', label: 'Cities', color: 'text-green-400' },
                  ].map(({ num, label, color }, i) => (
                    <div key={i}>
                      <div className={`text-2xl sm:text-3xl font-black ${color} mb-1`}>{num}</div>
                      <div className="text-gray-400 text-[10px] sm:text-xs">{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ════════════════════════════════════════════════════
              SECTION 6 — STAY UPDATED (Newsletter look)
          ════════════════════════════════════════════════════ */}
          <section className="py-12 px-4 bg-white">
            <div className="container mx-auto max-w-md">
              <div className="bg-gradient-to-r from-red-600 to-rose-500 rounded-3xl p-6 text-white">
                <p className="font-black text-lg mb-1">Stay Updated!</p>
                <p className="text-rose-100 text-xs mb-4">Get latest offers and beauty tips straight to your inbox.</p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="flex-1 rounded-xl px-4 py-2.5 text-sm text-gray-800 outline-none"
                    disabled
                  />
                  <button className="bg-white text-red-600 rounded-xl px-4 flex items-center justify-center">
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* ════════════════════════════════════════════════════
              SECTION 7 — FINAL CTA
          ════════════════════════════════════════════════════ */}
          <section className="py-12 px-4" style={{ background: '#fafafa' }}>
            <div className="container mx-auto max-w-md text-center">
              <p className="text-2xl mb-2">✨</p>
              <h2 className="text-xl font-black text-gray-900 mb-2" style={{ fontFamily: "'Syne',sans-serif" }}>
                Ready to look your best?
              </h2>
              <p className="text-sm text-gray-500 mb-6">Join thousands booking premium salon services every day.</p>
              <Button
                size="lg"
                onClick={() => { setSelectedRole('customer'); setShowAuth(true); }}
                className="btn-book text-white px-10 py-6 text-base font-bold rounded-2xl"
              >
                <Zap className="w-5 h-5 mr-2" /> Book Now — It's Free
              </Button>
            </div>
          </section>
        </>
      ) : (
        /* ════════════════════════════════════════════════════
            AUTH CARD — unchanged logic, just centered nicely
        ════════════════════════════════════════════════════ */
        <div className="pt-28 pb-16 px-4" style={{ background: 'linear-gradient(180deg,#fff5f2 0%,#fffaf8 100%)', minHeight: '100vh' }}>
        <div className="max-w-md mx-auto animate-scale-in">
          <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden">
            <div className={`h-1.5 w-full ${selectedRole === 'customer' ? 'bg-gradient-to-r from-red-500 to-rose-400' : 'bg-gradient-to-r from-purple-600 to-violet-500'}`} />
            <CardHeader className="pt-6 pb-2">
              <CardTitle className="text-xl sm:text-2xl text-center font-bold">
                {selectedRole === 'customer' ? '👋 Welcome Back' : '🚀 Salon Partner Registration'}
              </CardTitle>
              <CardDescription className="text-center text-sm mt-1">
                {selectedRole === 'customer'
                  ? 'Login with OTP — No password needed'
                  : 'Verify your phone to get started'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedRole === 'customer' && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2">Your Name</label>
                    <Input
                      data-testid="name-input"
                      placeholder="Enter your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Gender *</label>
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant={gender === 'male' ? 'default' : 'outline'}
                        className="flex-1"
                        onClick={() => setGender('male')}
                      >
                        Male
                      </Button>
                      <Button
                        type="button"
                        variant={gender === 'female' ? 'default' : 'outline'}
                        className="flex-1"
                        onClick={() => setGender('female')}
                      >
                        Female
                      </Button>
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Phone Number</label>
                <div className="flex gap-2">
                  <div className="flex items-center px-3 bg-gray-100 border border-r-0 rounded-l-md text-sm text-gray-600">
                    +91
                  </div>
                  <Input
                    data-testid="phone-input"
                    type="tel"
                    inputMode="numeric"
                    placeholder="10-digit number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    maxLength={10}
                    disabled={otpSent}
                    className="rounded-l-none flex-1"
                  />
                  {!otpSent && (
                    <Button
                      data-testid="send-otp-btn"
                      onClick={handleSendOTP}
                      disabled={loading || phone.length < 10}
                    >
                      {loading ? 'Sending...' : 'Send OTP'}
                    </Button>
                  )}
                </div>
              </div>

              {otpSent && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-2">Enter OTP</label>
                    <Input
                      data-testid="otp-input"
                      type="tel"
                      inputMode="numeric"
                      placeholder="6-digit OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      autoFocus
                      className="text-center text-lg tracking-widest"
                    />
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Didn't receive OTP?</span>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      onClick={handleResendOTP}
                      disabled={resendTimer > 0 || loading}
                      className="p-0 h-auto"
                    >
                      {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                    </Button>
                  </div>
                </div>
              )}

              {otpSent && (
                <Button
                  data-testid="verify-otp-btn"
                  className="w-full"
                  onClick={handleVerifyOTP}
                  disabled={loading || otp.length < 6}
                >
                  {loading ? 'Verifying...' : 'Verify & Continue'}
                </Button>
              )}

              {/* Google Sign-In for customers only */}
              {selectedRole === 'customer' && !otpSent && (
                <>
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-gray-500">Or continue with</span>
                    </div>
                  </div>
                  <Button
                    data-testid="google-signin-btn"
                    type="button"
                    variant="outline"
                    className="w-full flex items-center justify-center gap-2"
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {loading ? 'Signing in...' : 'Continue with Google'}
                  </Button>
                </>
              )}

              {selectedRole === 'salon' && (
                <div className="text-center pt-1">
                  <Button
                    variant="ghost"
                    className="w-full text-purple-500 hover:text-purple-700 text-sm"
                    onClick={() => navigate('/salon/login')}
                    data-testid="partner-login-link"
                  >
                    Already a Partner? Login
                  </Button>
                </div>
              )}

              <Button data-testid="back-btn" variant="outline" className="w-full" onClick={handleBack}>
                Back
              </Button>
            </CardContent>
          </Card>
        </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default Landing;
